import { atom, useAtom, useAtomValue } from "jotai";

import { defaultAccountState } from "#/config/accounts";
import { useMarket } from "#/store/app/portfolio/market";
import { type AccountStateByPortfolioSymbol } from "#/types/accounts";
import { PortfolioSymbol, type PortfolioSymbolType } from "#/types/markets";

import { useAccount } from "./account";

export const accountStateAtom =
  atom<AccountStateByPortfolioSymbol>(defaultAccountState);

export const accountValueAtom = atom((get) => {
  const accountState = get(accountStateAtom);
  return Object.values(accountState).reduce(
    (sum, { equity }) => sum + equity,
    0,
  );
});

export const useAccountState = (portfolio?: PortfolioSymbolType) => {
  const { market } = useMarket();
  const { account } = useAccount();

  const [accountState, setAccountState] = useAtom(accountStateAtom);
  const accountValue = useAtomValue(accountValueAtom);

  const effectivePortfolio: PortfolioSymbolType =
    portfolio ?? market.underlying;

  const unallocated = account
    ? accountState[PortfolioSymbol.USDC]
    : defaultAccountState[PortfolioSymbol.USDC];
  const portfolioState = account
    ? accountState[effectivePortfolio]
    : unallocated;
  const hasZeroEquity = account ? portfolioState.equity <= 0 : true;
  const hasTransferableEquity = account ? unallocated.equity > 0 : false;
  const hasEquitySomewhere = account
    ? Object.values(accountState).some((item) => item.equity > 0)
    : false;

  return {
    accountState: account ? accountState : defaultAccountState,
    accountValue: account ? accountValue : 0,
    hasZeroEquity,
    portfolioState,
    hasEquitySomewhere,
    hasTransferableEquity,
    setAccountState,
  };
};
